import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { fetchMetrics, fetchSubmissions } from '../../api/portal-api.js';
import type { SubmissionMetrics, SubmissionListResponse } from '@qdf/shared';

function formatDuration(minutes: number): string {
  if (minutes < 1) return '<1 min';
  if (minutes < 60) return `${Math.round(minutes)} min`;
  const hours = Math.floor(minutes / 60);
  const mins = Math.round(minutes % 60);
  return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
}

function fmtCurrency(amount: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0 }).format(amount);
}

function fmtCompact(amount: number): string {
  if (amount >= 1_000_000) return `$${(amount / 1_000_000).toFixed(1)}M`;
  if (amount >= 1_000) return `$${(amount / 1_000).toFixed(0)}K`;
  return fmtCurrency(amount);
}

export function PortalDashboard() {
  const navigate = useNavigate();
  const [timeRange, setTimeRange] = useState<'today' | 'week' | 'month' | 'all'>('week');
  const [company, setCompany] = useState<'' | 'leftmain' | 'newwestern'>('');

  const { data: metrics, isLoading: metricsLoading } = useQuery<SubmissionMetrics>({
    queryKey: ['portal-metrics', timeRange, company],
    queryFn: () => fetchMetrics(timeRange, company || undefined),
    refetchInterval: 30_000,
  });

  const { data: recentData } = useQuery<SubmissionListResponse>({
    queryKey: ['portal-recent-submissions'],
    queryFn: () => fetchSubmissions({ limit: 5 }),
    refetchInterval: 15_000,
  });

  if (metricsLoading || !metrics) {
    return (
      <div style={s.loadingWrap}>
        <div style={s.spinner} />
        <p style={{ color: '#64748b', marginTop: '16px', fontSize: '14px' }}>Loading dashboard...</p>
      </div>
    );
  }

  // formCount/apiCount = submissions received in the selected range,
  // so their sum is the "New" figure for every range including All Time.
  const timeValue = metrics.formCount + metrics.apiCount;
  const periodLabel =
    timeRange === 'today' ? 'today' :
    timeRange === 'week' ? 'this week' :
    timeRange === 'month' ? 'this month' : 'all time';

  const totalAll = metrics.formCount + metrics.apiCount;
  const pendingCount = metrics.pendingReview;
  const recentItems = recentData?.items || [];

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Dashboard</h1>
          <p style={s.subtitle}>Welcome back — here's what's happening with your submissions.</p>
        </div>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <div style={s.timeToggle}>
            {([['', 'All Companies'], ['leftmain', 'Left Main'], ['newwestern', 'New Western']] as const).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setCompany(key)}
                style={{ ...s.timeBtn, ...(company === key ? s.timeBtnActive : {}) }}
              >
                {label}
              </button>
            ))}
          </div>
          <div style={s.timeToggle}>
            {(['today', 'week', 'month', 'all'] as const).map(t => (
              <button
                key={t}
                onClick={() => setTimeRange(t)}
                style={{ ...s.timeBtn, ...(timeRange === t ? s.timeBtnActive : {}) }}
              >
                {t === 'today' ? 'Today' : t === 'week' ? 'This Week' : t === 'month' ? 'This Month' : 'All Time'}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ─── Row 1: Submission Status Counts ─── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '14px', marginBottom: '16px' }}>
        <MiniCard icon="📥" label="New" value={timeValue} sub={`received ${periodLabel}`} accent="#4338ca" />
        <MiniCard icon="⏳" label="Pending" value={pendingCount} sub={pendingCount > 0 ? '⚠ needs attention' : 'all clear'} accent="#d97706" highlight={pendingCount > 0} />
        <MiniCard icon="✅" label="Approved" value={metrics.approved} sub={`approved ${periodLabel}`} accent="#059669" />
        <MiniCard icon="✕" label="Rejected" value={metrics.rejected} sub={`denied ${periodLabel}`} accent="#dc2626" />
        <MiniCard icon="❌" label="Sync Failed" value={metrics.syncFailed} sub="needs resync" accent="#ef4444" highlight={metrics.syncFailed > 0} />
      </div>

      {/* ─── Row 2: FUNDING WINS — Big Dollar Amounts ─── */}
      <div style={{
        background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
        borderRadius: '16px', padding: '28px 32px', marginBottom: '16px',
        display: 'grid', gridTemplateColumns: '1fr 1px 1fr', gap: '0', alignItems: 'center',
        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
      }}>
        {/* Approved / Fund Out */}
        <div style={{ textAlign: 'center' as const, padding: '0 24px' }}>
          <div style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '1px', marginBottom: '8px' }}>
            💵 Funded Out Total
          </div>
          <div style={{
            fontSize: '48px', fontWeight: 800, letterSpacing: '-2px', lineHeight: 1,
            background: 'linear-gradient(135deg, #34d399, #059669)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          } as React.CSSProperties}>
            {fmtCurrency(metrics.totalApprovedAmount || 0)}
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '12px' }}>
            <div style={{ textAlign: 'center' as const }}>
              <div style={{ fontSize: '22px', fontWeight: 700, color: '#fff' }}>{metrics.approved}</div>
              <div style={{ fontSize: '10px', color: '#64748b' }}>Approved</div>
            </div>
            <div style={{ width: '1px', background: '#334155' }} />
            <div style={{ textAlign: 'center' as const }}>
              <div style={{ fontSize: '22px', fontWeight: 700, color: '#fff' }}>{metrics.syncedApproval || 0}</div>
              <div style={{ fontSize: '10px', color: '#64748b' }}>Synced</div>
            </div>
          </div>
        </div>

        {/* Divider */}
        <div style={{ height: '80%', background: 'linear-gradient(to bottom, transparent, #334155, transparent)' }} />

        {/* Funded / Fund In */}
        <div style={{ textAlign: 'center' as const, padding: '0 24px' }}>
          <div style={{ fontSize: '12px', color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '1px', marginBottom: '8px' }}>
            💰 Funded In Total
          </div>
          <div style={{
            fontSize: '48px', fontWeight: 800, letterSpacing: '-2px', lineHeight: 1,
            background: 'linear-gradient(135deg, #5eead4, #14b8a6)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
          } as React.CSSProperties}>
            {fmtCurrency(metrics.totalFundedAmount || 0)}
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginTop: '12px' }}>
            <div style={{ textAlign: 'center' as const }}>
              <div style={{ fontSize: '22px', fontWeight: 700, color: '#fff' }}>{metrics.funded}</div>
              <div style={{ fontSize: '10px', color: '#64748b' }}>Funded</div>
            </div>
            <div style={{ width: '1px', background: '#334155' }} />
            <div style={{ textAlign: 'center' as const }}>
              <div style={{ fontSize: '22px', fontWeight: 700, color: '#fff' }}>{metrics.syncedFunding || 0}</div>
              <div style={{ fontSize: '10px', color: '#64748b' }}>Synced</div>
            </div>
          </div>
        </div>
      </div>

      {/* ─── Row 3: SLA + Pipeline + Channels ─── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px', marginBottom: '16px' }}>
        {/* SLA Performance */}
        <div style={s.card}>
          <h2 style={s.cardTitle}>⏱️ SLA Performance</h2>
          <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '12px', marginTop: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', color: '#64748b' }}>Avg Response</span>
              <span style={{
                fontSize: '18px', fontWeight: 700,
                color: metrics.slaMetMinutes !== null && metrics.slaMetMinutes <= 30 ? '#059669' :
                       metrics.slaMetMinutes !== null && metrics.slaMetMinutes <= 60 ? '#d97706' :
                       metrics.slaMetMinutes !== null ? '#dc2626' : '#94a3b8',
              }}>
                {metrics.slaMetMinutes !== null ? formatDuration(metrics.slaMetMinutes) : '—'}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', color: '#64748b' }}>SLA Breaches</span>
              <span style={{ fontSize: '18px', fontWeight: 700, color: metrics.slaBreaches > 0 ? '#dc2626' : '#059669' }}>
                {metrics.slaBreaches}
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ fontSize: '12px', color: '#64748b' }}>Oldest Pending</span>
              <span style={{
                fontSize: '18px', fontWeight: 700,
                color: metrics.oldestPendingMinutes !== null && metrics.oldestPendingMinutes > 30 ? '#dc2626' :
                       metrics.oldestPendingMinutes !== null && metrics.oldestPendingMinutes > 15 ? '#d97706' : '#059669',
              }}>
                {metrics.oldestPendingMinutes !== null ? formatDuration(metrics.oldestPendingMinutes) : 'None'}
              </span>
            </div>
            {metrics.oldestPendingMinutes !== null && metrics.oldestPendingMinutes > 30 && (
              <div style={{ background: '#fef2f2', color: '#dc2626', padding: '6px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: 600 }}>
                🚨 Exceeds SLA target (30 min)
              </div>
            )}
          </div>
        </div>

        {/* Status Pipeline */}
        <div style={s.card}>
          <h2 style={s.cardTitle}>Status Pipeline</h2>
          <div style={{ display: 'flex', flexDirection: 'column' as const, gap: '2px', marginTop: '12px' }}>
            <PipeItem label="New" count={pendingCount} color="#3b82f6" onClick={() => navigate('/portal/submissions?status=new')} />
            <PipeItem label="Approved" count={metrics.approved} color="#10b981" onClick={() => navigate('/portal/submissions?status=approved')} />
            <PipeItem label="Rejected" count={metrics.rejected} color="#ef4444" onClick={() => navigate('/portal/submissions?status=rejected')} />
            <PipeItem label="Synced (Decision)" count={metrics.syncedApproval || 0} color="#8b5cf6" onClick={() => navigate('/portal/submissions?status=synced')} />
            <PipeItem label="Funded" count={metrics.funded} color="#14b8a6" onClick={() => navigate('/portal/submissions?status=funded')} />
            <PipeItem label="Synced (Funding)" count={metrics.syncedFunding || 0} color="#7c3aed" onClick={() => navigate('/portal/submissions?status=funded')} />
            <PipeItem label="Sync Failed" count={metrics.syncFailed} color="#f87171" onClick={() => navigate('/portal/submissions?status=sync_failed')} />
          </div>
        </div>

        {/* Channels */}
        <div style={s.card}>
          <h2 style={s.cardTitle}>Submission Channels</h2>
          <div style={{ marginTop: '12px' }}>
            <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
              <div style={{ flex: 1, textAlign: 'center' as const }}>
                <div style={{ fontSize: '28px', fontWeight: 800, color: '#4338ca' }}>{metrics.formCount}</div>
                <div style={{ fontSize: '11px', color: '#64748b' }}>Onboarding Forms</div>
                <div style={{ height: '4px', background: '#f1f5f9', borderRadius: '2px', marginTop: '6px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', borderRadius: '2px', background: '#4338ca', width: totalAll > 0 ? `${(metrics.formCount / totalAll) * 100}%` : '0%' }} />
                </div>
              </div>
              <div style={{ width: '1px', background: '#e2e8f0' }} />
              <div style={{ flex: 1, textAlign: 'center' as const }}>
                <div style={{ fontSize: '28px', fontWeight: 800, color: '#7c3aed' }}>{metrics.apiCount}</div>
                <div style={{ fontSize: '11px', color: '#64748b' }}>API Disbursements</div>
                <div style={{ height: '4px', background: '#f1f5f9', borderRadius: '2px', marginTop: '6px', overflow: 'hidden' }}>
                  <div style={{ height: '100%', borderRadius: '2px', background: '#7c3aed', width: totalAll > 0 ? `${(metrics.apiCount / totalAll) * 100}%` : '0%' }} />
                </div>
              </div>
            </div>
            <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '10px', textAlign: 'center' as const, fontSize: '12px', color: '#94a3b8' }}>
              Total: <strong style={{ color: '#334155' }}>{totalAll}</strong> submissions
            </div>
          </div>
        </div>
      </div>

      {/* ─── Row 4: Recent Submissions ─── */}
      <div style={s.card}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
          <h2 style={s.cardTitle}>Recent Submissions</h2>
          <button onClick={() => navigate('/portal/submissions')} style={s.viewAllBtn}>View All →</button>
        </div>
        {recentItems.length === 0 ? (
          <div style={{ textAlign: 'center' as const, padding: '24px', color: '#94a3b8' }}>No submissions yet</div>
        ) : (
          <table style={s.table}>
            <thead>
              <tr>
                <th style={s.th}>ID</th>
                <th style={s.th}>Type</th>
                <th style={s.th}>Name / Property</th>
                <th style={s.th}>Status</th>
                <th style={s.th}>Close</th>
                <th style={s.th}>Date</th>
              </tr>
            </thead>
            <tbody>
              {recentItems.map(item => (
                <tr key={item.submission_id} style={s.tr} onClick={() => navigate(`/portal/submissions/${item.submission_id}`)}>
                  <td style={s.td}><span style={s.idBadge}>{item.submission_id}</span></td>
                  <td style={s.td}>
                    <span style={{
                      padding: '2px 7px', borderRadius: '4px', fontSize: '11px', fontWeight: 600,
                      background: item.submission_type === 'form' ? '#eef2ff' : '#f5f3ff',
                      color: item.submission_type === 'form' ? '#4338ca' : '#7c3aed',
                    }}>
                      {item.submission_type === 'form' ? 'Form' : 'API'}
                    </span>
                  </td>
                  <td style={s.td}>
                    <div style={{ fontWeight: 500, color: '#1e293b', fontSize: '13px' }}>
                      {item.full_name || item.property_address_1 || '—'}
                    </div>
                  </td>
                  <td style={s.td}><StatusBadge status={item.status} /></td>
                  <td style={{
                    ...s.td, fontSize: '12px', fontWeight: 500,
                    color: item.projected_close_date ? (new Date(item.projected_close_date + 'T00:00:00') < new Date(new Date().toDateString()) ? '#dc2626' : '#1e293b') : '#cbd5e1',
                  }}>
                    {item.projected_close_date
                      ? new Date(item.projected_close_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                      : '—'}
                  </td>
                  <td style={{ ...s.td, color: '#64748b', fontSize: '12px' }}>
                    {new Date(item.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────────

function MiniCard({ icon, label, value, sub, accent, highlight }: {
  icon: string; label: string; value: number; sub: string; accent: string; highlight?: boolean;
}) {
  return (
    <div style={{
      background: '#fff', borderRadius: '12px', padding: '16px 18px',
      border: highlight ? `2px solid ${accent}` : '1px solid #e2e8f0',
      boxShadow: highlight ? `0 0 0 3px ${accent}15` : '0 1px 3px rgba(0,0,0,0.04)',
    }}>
      <div style={{ fontSize: '11px', color: '#64748b', fontWeight: 500, marginBottom: '4px' }}>{icon} {label}</div>
      <div style={{ fontSize: '28px', fontWeight: 800, color: '#1e293b', letterSpacing: '-1px', lineHeight: 1 }}>{value}</div>
      <div style={{ fontSize: '10px', color: highlight ? accent : '#94a3b8', marginTop: '4px', fontWeight: highlight ? 600 : 400 }}>{sub}</div>
    </div>
  );
}

function PipeItem({ label, count, color, onClick }: { label: string; count: number; color: string; onClick: () => void }) {
  return (
    <div onClick={onClick} style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      padding: '7px 10px', borderRadius: '6px', cursor: 'pointer',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: color }} />
        <span style={{ fontSize: '12px', color: '#475569' }}>{label}</span>
      </div>
      <span style={{ fontSize: '15px', fontWeight: 700, color: '#1e293b' }}>{count}</span>
    </div>
  );
}

const STATUS_MAP: Record<string, { label: string; color: string; bg: string }> = {
  new: { label: 'New', color: '#1d4ed8', bg: '#dbeafe' },
  in_review: { label: 'In Review', color: '#b45309', bg: '#fef3c7' },
  approved: { label: 'Approved', color: '#047857', bg: '#d1fae5' },
  rejected: { label: 'Rejected', color: '#b91c1c', bg: '#fee2e2' },
  synced: { label: 'Synced', color: '#6d28d9', bg: '#ede9fe' },
  sync_failed: { label: 'Sync Failed', color: '#dc2626', bg: '#fee2e2' },
  funded: { label: 'Funded', color: '#0d9488', bg: '#ccfbf1' },
};

function StatusBadge({ status }: { status: string }) {
  const info = STATUS_MAP[status] || { label: status, color: '#475569', bg: '#f1f5f9' };
  return (
    <span style={{ padding: '2px 8px', borderRadius: '10px', fontSize: '11px', fontWeight: 600, background: info.bg, color: info.color }}>
      {info.label}
    </span>
  );
}

// ─── Styles ────────────────────────────────────────────────────────────────

const s: Record<string, React.CSSProperties> = {
  page: { padding: '24px 32px', maxWidth: '1200px', margin: '0 auto', fontFamily: "'Inter', -apple-system, sans-serif" },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px', flexWrap: 'wrap' as const, gap: '12px' },
  title: { fontSize: '24px', fontWeight: 700, color: '#0f172a', margin: 0 },
  subtitle: { fontSize: '13px', color: '#64748b', margin: '3px 0 0' },
  timeToggle: { display: 'flex', background: '#f1f5f9', borderRadius: '8px', padding: '2px', gap: '2px' },
  timeBtn: { padding: '6px 14px', borderRadius: '6px', border: 'none', background: 'transparent', color: '#64748b', fontSize: '12px', fontWeight: 500, cursor: 'pointer' },
  timeBtnActive: { background: '#fff', color: '#1e293b', boxShadow: '0 1px 3px rgba(0,0,0,0.08)' },
  card: { background: '#fff', borderRadius: '12px', padding: '20px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.04)' },
  cardTitle: { fontSize: '14px', fontWeight: 600, color: '#1e293b', margin: 0 },
  viewAllBtn: { background: 'none', border: 'none', color: '#4338ca', fontSize: '12px', fontWeight: 600, cursor: 'pointer' },
  table: { width: '100%', borderCollapse: 'collapse' as const },
  th: { textAlign: 'left' as const, padding: '8px 12px', color: '#94a3b8', fontSize: '10px', fontWeight: 600, textTransform: 'uppercase' as const, letterSpacing: '0.5px', borderBottom: '1px solid #f1f5f9' },
  tr: { cursor: 'pointer', transition: 'background 0.1s', borderBottom: '1px solid #f8fafc' },
  td: { padding: '10px 12px', fontSize: '13px', color: '#334155' },
  idBadge: { fontFamily: 'monospace', fontSize: '12px', color: '#4338ca', fontWeight: 600 },
  loadingWrap: { display: 'flex', flexDirection: 'column' as const, alignItems: 'center', justifyContent: 'center', minHeight: '60vh' },
  spinner: { width: '36px', height: '36px', border: '3px solid #e2e8f0', borderTop: '3px solid #4338ca', borderRadius: '50%', animation: 'spin 1s linear infinite' },
};
